import { Modal, Pressable, ScrollView, Text, View, useWindowDimensions } from 'react-native';
import { X } from 'lucide-react-native';
import Animated, { FadeIn, FadeInDown, FadeInUp, FadeOut } from 'react-native-reanimated';

import { colors, radius, typography } from '@/theme/tokens';

interface Props {
  visible: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  size?: 'sm' | 'md' | 'lg';
  footer?: React.ReactNode;
}

const SIZES: Record<NonNullable<Props['size']>, number> = {
  sm: 420,
  md: 560,
  lg: 760,
};

export function Sheet({ visible, onClose, title, subtitle, children, size = 'md', footer }: Props) {
  const { width } = useWindowDimensions();
  const wide = width >= 920;
  const maxWidth = SIZES[size];

  return (
    <Modal visible={visible} transparent animationType="none" onRequestClose={onClose}>
      <Animated.View
        entering={FadeIn.duration(180)}
        exiting={FadeOut.duration(180)}
        style={{
          flex: 1,
          backgroundColor: 'rgba(15, 23, 42, 0.5)',
          justifyContent: wide ? 'center' : 'flex-end',
          alignItems: 'center',
          // @ts-ignore web only
          backdropFilter: 'blur(4px)',
        }}
      >
        <Pressable style={{ position: 'absolute', inset: 0 }} onPress={onClose} />
        <Animated.View
          entering={wide ? FadeInUp.duration(220) : FadeInDown.duration(220)}
          style={{
            width: '100%',
            maxWidth,
            maxHeight: '92%',
            backgroundColor: colors.white,
            borderRadius: wide ? radius['4xl'] : 0,
            borderTopLeftRadius: radius['4xl'],
            borderTopRightRadius: radius['4xl'],
            overflow: 'hidden',
            // @ts-ignore web only
            boxShadow: '0 24px 48px -12px rgba(15, 23, 42, 0.35)',
          }}
        >
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              paddingHorizontal: 24,
              paddingTop: 24,
              paddingBottom: 12,
              gap: 12,
            }}
          >
            <View style={{ flex: 1, gap: 4 }}>
              <Text
                style={{
                  fontFamily: typography.display.fontFamily,
                  fontSize: 22,
                  fontWeight: typography.weights.extrabold,
                  color: colors.ink[900],
                  letterSpacing: -0.4,
                }}
              >
                {title}
              </Text>
              {subtitle ? <Text style={{ color: colors.ink[500], fontSize: 13 }}>{subtitle}</Text> : null}
            </View>
            <Pressable
              onPress={onClose}
              style={({ hovered }) => ({
                width: 36,
                height: 36,
                borderRadius: 12,
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: hovered ? colors.ink[100] : colors.crema[50],
                // @ts-ignore web only
                cursor: 'pointer',
              })}
            >
              <X size={18} color={colors.ink[600]} />
            </Pressable>
          </View>

          <ScrollView
            contentContainerStyle={{ paddingHorizontal: 24, paddingBottom: footer ? 16 : 28, gap: 14 }}
            showsVerticalScrollIndicator={false}
          >
            {children}
          </ScrollView>

          {footer ? (
            <View
              style={{
                padding: 20,
                borderTopWidth: 1,
                borderTopColor: colors.ink[100],
                backgroundColor: colors.crema[50],
                flexDirection: 'row',
                justifyContent: 'flex-end',
                gap: 10,
              }}
            >
              {footer}
            </View>
          ) : null}
        </Animated.View>
      </Animated.View>
    </Modal>
  );
}
